/**
 * ⚠️ FORKED COMPONENT — DO NOT MERGE BACK ⚠️
 *
 * LR DIRECT — Bid action subscription gate
 *
 * Renders an upgrade CTA when the current user's billing tier cannot bid
 * on the given load. Returns null to let the caller render the normal bid form.
 *
 * Pricing model (v5, single marketed tier):
 *   board_only ($59.99/mo)               → load board only, no LR Direct bidding
 *   stripe ($99.99/mo, Full Access)      → bid on private + open market
 *   lr_direct ($249.99, RETIRED legacy)  → bid on private + open market

 *
 * Admins, demo users, and shippers (with a non-upsell message) bypass the
 * paid gate. All upgrade CTAs route through the shared LRDirectGateContext
 * paywall — no `/pricing` redirect.
 */
import { Lock, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBillingMode } from "@/hooks/useBillingMode";
import { useLRDirectGate } from "@/contexts/LRDirectGateContext";

interface BidActionGateProps {
  visibilityMode: "private_only" | "open_market" | string;
  loadId: string;
}

export function BidActionGate({ visibilityMode }: BidActionGateProps): JSX.Element | null {
  const { user, profile, isAdmin, isDemoUser } = useAuth();
  const { hasPrivateNetworkAccess, hasLRDirectFullAccess, isLoading } = useBillingMode();
  const { openGate } = useLRDirectGate();
  const navigate = useNavigate();

  if (!user || isLoading) return null;
  if (isAdmin || isDemoUser) return null;

  // Shippers can browse the LR Direct board but cannot bid — show a clear
  // non-upsell message instead of routing them to broker pricing.
  if (profile?.user_type === "shipper") {
    return (
      <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Briefcase className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <div className="text-xs leading-relaxed">
            <p className="font-bold text-foreground">Broker account required to bid</p>
            <p className="text-muted-foreground mt-1">
              Bidding on LR Direct loads is available to broker accounts only.
              As a shipper, you can browse the board and post your own loads.
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate("/lr-direct/post/start")}
          variant="outline"
          className="w-full h-11 text-sm font-bold uppercase tracking-wide"
        >
          Post a Load
        </Button>
      </div>
    );
  }

  // Full-access broker ($99.99 'stripe', 'comped', legacy 'lr_direct') — no gate.
  // Covers both private-network and open-market loads; network membership for
  // private loads is still enforced server-side by submit_direct_bid.
  if (hasLRDirectFullAccess) return null;

  // Retired legacy private-only tier: still allowed on private-network loads.
  if (hasPrivateNetworkAccess && visibilityMode === "private_only") return null;

  // board_only / free (and retired private tier on open-market loads):
  // no LR Direct bidding until they move to Full Access.
  return (
    <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Lock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <div className="text-xs leading-relaxed">
          <p className="font-bold text-foreground">Upgrade to bid on LR Direct loads</p>
          <p className="text-muted-foreground mt-1">
            Full Access ($99.99/mo) unlocks bidding on every LR Direct load you can see —
            private shipper networks you've been invited to and open-marketplace freight.
          </p>
        </div>
      </div>
      <Button
        onClick={() => openGate()}
        className="w-full h-11 text-sm font-bold uppercase tracking-wide bg-primary text-white hover:bg-primary/90"
      >
        <Lock className="h-4 w-4 mr-2" />
        Upgrade — $99.99/mo
      </Button>
    </div>
  );
}

